import React from "react";

import { replaceEmployees } from "EmployerApplication/Actions";
import { useGetCompanyContributionsQuery, useGetCurrentCompanyApplicationQuery } from "EmployerApplication/api";
import { ApplicationLayout } from "EmployerApplication/Components";
import { api, addEmployeesURL, reviewEmployeeEmailsURL } from "EmployerApplication/urls";
import createDecorator from "final-form-focus";
import { values, mapValues } from "lodash";
import { Form } from "react-final-form";
import { useDispatch, useSelector } from "react-redux";
import { withRouter } from "react-router-dom";
import AnalyticsTracking from "Utils/AnalyticsTracking";
import companyToken from "Utils/companyToken";
import fetchWithCSRFToken from "Utils/fetchWithCSRFToken";
import { validateRequired } from "Utils/Validations";
import { BasicLoading } from "ZeroRuby";

import { Table, HeaderTR, TH, TR, TD, EllipsisTD } from "Cobalt/TableV2";
import AgentHeader from "Common/Agent/AgentHeader";
import { TIERED_CONTRIBUTION_ROLES, TIERED_CONTRIBUTIONS_SORTING_ORDER } from "Common/Constants";
import { SIFinalFormFieldNoCol } from "Common/forms";
import { Row, Col } from "Common/Grid";
import NavigationControls from "Common/NavigationControls";
import { useThemeContext } from "Theme/Context";
import { Select } from "Theme/Select";
import { Surface, SurfaceSection, SurfaceHeader } from "Theme/Surface";

const focusOnErrors = createDecorator();

export const SelectContributionTiersForm = ({ history, showCobalt, companyContributions }) => {
  const dispatch = useDispatch();
  const employees = useSelector((state) => state.employees);

  const parseValues = (values) => ({
    company_id_token: companyToken(),
    employees: mapValues(values.employees, (e) => ({
      company_contribution_id: e.companyContributionId,
    })),
  });

  const submitEmployeeContributions = (values) => {
    fetchWithCSRFToken(api.updateEmployeeContributions, {
      method: "PUT",
      body: JSON.stringify({ ...parseValues(values) }),
    })
      .then((response) => {
        if (response.ok) {
          return response.json();
        }
        throw new Error(`Unable to save, HTTP ${response.status}`);
      })
      .then((response) => {
        // Update state
        dispatch(replaceEmployees(response.employees));

        // // Redirect user
        history.push(reviewEmployeeEmailsURL);
      });
  };

  const initialValues = (companyContributions) => ({
    employees: values(employees).reduce((employees, e) => {
      if (!e.is_eligible) return employees;
      employees[e.hash_id] = {};
      employees[e.hash_id].companyContributionId = companyContributions.find((contribution) => contribution?.role === e.contribution_tier_name)?.id;
      return employees;
    }, {}),
  });

  const contributionOptions = (companyContributions) =>
    companyContributions
      .filter((contribution) => contribution.active && !!contribution.role)
      .sort((a, b) => TIERED_CONTRIBUTIONS_SORTING_ORDER.indexOf(a.role) - TIERED_CONTRIBUTIONS_SORTING_ORDER.indexOf(b.role))
      .reduce((options, tier) => {
        const label = TIERED_CONTRIBUTION_ROLES[tier.role];
        return [...options, [tier.id, label]];
      }, []);

  const handleClickBack = () => {
    AnalyticsTracking.trackEvent("back_button_clicked", {
      location: "select_contribution_tiers_page",
    });
  };

  return (
    <Form onSubmit={submitEmployeeContributions} decorators={[focusOnErrors]} initialValues={initialValues(companyContributions)}>
      {({ handleSubmit, submitting }) => (
        <form onSubmit={handleSubmit}>
          {!showCobalt && <SurfaceHeader>Employee Contribution Tiers</SurfaceHeader>}

          <SurfaceSection title="Employee Contribution Tiers">
            <Row>
              <Col>
                <Table>
                  <thead>
                    <HeaderTR>
                      <TH width="200px">Name</TH>
                      <TH>Employee Contribution Tier</TH>
                      <TH fitContent />
                    </HeaderTR>
                  </thead>
                  <tbody>
                    {Object.values(employees)
                      .filter((employee) => employee.is_eligible)
                      .map((employee) => (
                        <TR key={employee.hash_id}>
                          <EllipsisTD>{employee.full_name}</EllipsisTD>
                          <TD>
                            <SIFinalFormFieldNoCol
                              name={`employees["${employee.hash_id}"].companyContributionId`}
                              component={Select}
                              componentProps={{
                                style: { maxWidth: "300px" },
                                noPlaceholder: true,
                                options: contributionOptions(companyContributions),
                              }}
                              validate={validateRequired}
                            />
                          </TD>
                          <TD />
                        </TR>
                      ))}
                  </tbody>
                </Table>
              </Col>
            </Row>
          </SurfaceSection>
          <NavigationControls
            backLink={addEmployeesURL}
            onClickBack={handleClickBack}
            onClickSave={() => {
              AnalyticsTracking.trackEvent("save_and_continue_select_contribution_tiers", {});

              handleSubmit();
            }}
            saveDisabled={submitting}
            submitting={submitting}
          />
        </form>
      )}
    </Form>
  );
};

export const SelectContributionTiersPage = ({ history }) => {
  const { intuit, cobalt } = useThemeContext();
  const showCobalt = cobalt && !intuit;
  const { data: companyApplication } = useGetCurrentCompanyApplicationQuery();
  const companyPolicyId = companyApplication?.id;
  const { data: companyContributions, isLoading } = useGetCompanyContributionsQuery(companyPolicyId, { skip: !companyPolicyId });

  if (isLoading) return <BasicLoading />;

  return (
    <Surface p={0} mx="auto">
      {showCobalt && <SurfaceHeader>Manage Employees</SurfaceHeader>}
      <AgentHeader>Please select the contribution amount each employee should receive by placing them into their correct tier.</AgentHeader>
      <SelectContributionTiersForm history={history} showCobalt={showCobalt} companyContributions={companyContributions} />
    </Surface>
  );
};

export default withRouter(SelectContributionTiersPage);
